import { Injectable, Injector, inject } from '@angular/core';
import { MissingTranslationHandler, MissingTranslationHandlerParams } from '@ngx-translate/core';
import { moduleForKey } from './i18n.config';
import { TranslationManagerService } from './translation-manager.service';

/**
 * Страхувальна сітка модульної системи перекладів.
 *
 * Якщо ключ не знайдено, дивимось на його неймспейс (`olx.title` -> модуль `olx`).
 * Коли такий модуль існує і ще не завантажений — тихо запускаємо його завантаження.
 * Щойно файл приїде, `TranslateService` емітить `onTranslationChange`, і `translate`-пайп
 * сам перемалює вже правильне значення.
 *
 * Повертаємо сам ключ, а не порожній рядок: порожній рядок ламає верстку
 * (зникають написи на кнопках), а видимий ключ одразу показує, що переклад загубився.
 */
@Injectable({ providedIn: 'root' })
export class LazyMissingTranslationHandler implements MissingTranslationHandler {
  private readonly injector = inject(Injector);

  /** Ключі, для яких уже ініціювали дозавантаження — щоб не спамити. */
  private readonly attempted = new Set<string>();

  public handle(params: MissingTranslationHandlerParams): string {
    const key = params.key;
    const module = moduleForKey(key);

    if (module && !this.attempted.has(key)) {
      this.attempted.add(key);
      // Ліниве отримання менеджера розриває цикл DI:
      // TranslateService -> MissingTranslationHandler -> TranslationManagerService -> TranslateService.
      const manager = this.injector.get(TranslationManagerService);
      if (!manager.isModuleLoaded(module)) {
        manager.loadModuleTranslations(module).subscribe();
      }
    }

    return key;
  }
}
